// Greetings (打招呼): walk past someone and their card shows in the top-right corner (their otter, Raft avatar, name,
// 🤖 for agents, where they are) with 「👋 打招呼」; they get 「X 跟你打招呼 👋」 with 「回招呼」. Who's on the card: the
// people I hear right now (the office's "hear", the same rule as chat), nearest first; it goes LINGER_MS after they
// leave earshot, and × keeps that person off it for DISMISS_MS. Key H greets (or answers the greeting on screen),
// never while typing. The office checks range and limits (src/office/greet.ts) and makes the greeter wave. Anyone's
// card also offers 「🎲 邀请玩…」 to the game where I am, only while I am at one (invite.js). Clicking someone on the map (public/pick.js) pins
// their card, near or far, with 「走过去」, until ×, Esc or another click. An agent's card also has 「请出去」
// (agent-door.js) for a person. Everyone's card has 「在 Raft 里私聊」 (raft-links.js), and (members, anywhere in the
// office) 「邀请到身边」: TA gets a card and their otter walks over to me (meetinvite.js, kind "here").
import { icon, label, put } from '/icons.js';
import { t, tr, has, onLang } from '/i18n.js';
const LINGER_MS = 4000, DISMISS_MS = 5 * 60_000, PAIR_GAP_MS = 30_000, NOTE_MS = 20_000, NEAR = 6.5;

const CSS = `#greetbox{position:fixed;top:12px;right:12px;z-index:15;display:flex;flex-direction:column;align-items:flex-end;gap:8px;width:250px;max-width:calc(100vw - 24px);pointer-events:none;font:14px/1.4 var(--ui-font,-apple-system,system-ui,sans-serif);color:#1b1410}
#greetbox>*{pointer-events:auto;box-sizing:border-box;width:100%;background:#fff8e7;border:3px solid #1b1410;border-radius:14px;box-shadow:0 3px 0 #1b1410}
#greetbox button{font:inherit;border:2px solid #1b1410;border-radius:999px;padding:4px 12px;background:#FDD03B;cursor:pointer;white-space:nowrap}
#greetbox button:disabled{background:#eee;color:#777;cursor:default}
#greetNote{display:none;align-items:center;gap:8px;padding:8px 10px;animation:greetPop .25s ease-out}
#greetNote .t{flex:1;min-width:0;word-break:break-word}
#greetCard{display:none;padding:8px 10px;gap:10px;align-items:center;position:relative}
#greetCard .av{position:relative;width:56px;height:56px;flex:none;background:#e9d8b4;border-radius:10px}
#greetCard canvas{width:56px;height:56px;image-rendering:pixelated;display:block}
#greetCard .pic{position:absolute;right:-6px;bottom:-6px;width:24px;height:24px;border-radius:50%;border:2px solid #1b1410;background:#fff;object-fit:cover}
#greetCard .info{flex:1;min-width:0;display:flex;flex-direction:column;gap:3px}
#greetCard .nm{font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;padding-right:16px}
#greetCard .wh{font-size:12px;color:#666;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
#greetCard .stl{font-size:12px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
#greetCard .stl.away{color:#a16207} #greetCard .stl.busy{color:#b45309} #greetCard .stl.dnd{color:#dc2626}
#greetCard.dnd{border-color:#ef4444;box-shadow:0 3px 0 #ef4444}
#greetCard .row{display:flex;flex-wrap:wrap;gap:6px;align-items:center}
#greetCard .inv,#greetCard .walk,#greetCard .kick,#greetCard .sec,#greetCard .call{background:#fff!important;padding:4px 10px!important}
#greetCard .x{position:absolute;right:4px;top:2px;border:0!important;background:none!important;padding:0 4px!important;font-size:16px;color:#666;box-shadow:none}
#greetCard .rf{font:inherit;font-size:13px;border:2px solid #1b1410;border-radius:999px;padding:4px 10px;background:#fff;color:#1b1410;text-decoration:none;white-space:nowrap;display:inline-flex;align-items:center}
#greetCard .rf[hidden]{display:none}
#greetCard .more{background:#fff!important;padding:2px 8px!important;font-size:12px}
@keyframes greetPop{from{transform:translateY(-6px);opacity:0}to{transform:none;opacity:1}}
@media (max-width:560px){#greetbox{width:210px;top:8px;right:8px}#greetCard .av,#greetCard canvas{width:44px;height:44px}}`;

/**
 * @param o {me, peers, hearing, caster, zoneAt, send, lookOf, buildSheet, otterImgs, agentName, toast, invite?}: getters for
 *   the app's state (me / peers map / the ids I hear / the broadcaster), and what the card needs to draw an otter;
 *   invite (invite.js) adds 「🎲 邀请玩…」 on the card while I am at a game (else nothing); here (meetinvite.js) 「邀请到身边」 (forCard(p): null to hide); door (agent-door.js) 「请出去」 on an agent's (forCard(p): null to hide); talk (secretary.js) 「和 TA 说话」 on an agent's; raftProfile(p) (raft-links.js) is their Raft page, for 「在 Raft 里私聊」. status(p) (public/status.js statusOf): their 离开 / 忙碌 /
 *   请勿打扰, with statusText(s) / statusIcon(s) to show it;
 *   someone on 请勿打扰 near me isn't heard, but their card still shows (marked 请勿打扰, no 打招呼).
 * @returns {{msg(m): void, pin(p), forCard(p)}} msg: the office's greet / greet_ok / greet_err messages; forCard: 打招呼 on the
 *   小窗's card (public/pip.js)
 */
export function initGreet(o) {
  const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
  const box = document.createElement('div'); box.id = 'greetbox';
  box.innerHTML = `<div id="greetNote"><span class="t"></span><button></button></div>
    <div id="greetCard"><button class="x">×</button><div class="av"><canvas width="32" height="32"></canvas><img class="pic" alt="" hidden></div>
    <div class="info"><span class="nm"></span><span class="stl" hidden></span><span class="wh"></span><div class="row"><button class="go"></button><button class="walk" hidden></button><button class="inv" hidden></button><button class="here" hidden></button><button class="sec" hidden></button><button class="call" hidden></button><button class="kick" hidden></button><a class="rf" target="_blank" rel="noopener" hidden></a><button class="more" hidden></button></div></div></div>`;
  document.body.appendChild(box);
  label(box.querySelector('#greetNote button'), 'wave', t('greet.reply'));
  const note = box.querySelector('#greetNote'), card = box.querySelector('#greetCard'), q = (s) => card.querySelector(s);
  tr(q('.x'), null, undefined, { title: 'greet.dismiss' });
  const cv = q('canvas'), g = cv.getContext('2d'), pic = q('.pic');

  const key = (p) => p.sub || p.id;      // a person (Raft login) across reconnects
  const dismissed = new Map();          // key -> until
  const sentAt = new Map();             // key -> when I greeted them (the office allows one per PAIR_GAP_MS)
  const gotAt = new Map();              // key -> when they greeted me (my next greet to them is a reply)
  let pinned = null; // someone clicked on the map (public/pick.js): on the card whether I hear them or not
  let shown = null, goneAt = 0, pick = 0, noteFrom = null, noteTimer = 0, sheetSig = '', sheet = null;

  /** The people on the card's list: those I hear (not just because they broadcast), not dismissed, nearest first. */
  function candidates(now) {
    const me = o.me(); if (!me) return [];
    const hear = o.hearing(), mz = o.zoneAt(me.x, me.y)?.id ?? null, out = [];
    for (const p of o.peers().values()) {
      const d = Math.hypot(p.x - me.x, p.y - me.y), pz = o.zoneAt(p.x, p.y)?.id ?? null;
      const dndNear = o.status?.(p)?.s === 'dnd' && d <= (o.dndNear ?? NEAR) && !pz && !mz; // on the deck: not heard, but shown (请勿打扰)
      if ((!hear.has(p.id) && !dndNear) || (dismissed.get(key(p)) ?? 0) > now) continue;
      if (p.id === o.caster() && !(mz && mz === pz) && d > NEAR) continue;
      out.push([d, p]);
    }
    return out.sort((a, b) => a[0] - b[0]).map(([, p]) => p);
  }

  function place() {
    // Game huts and 五子棋 open their panels in this corner (and on a phone the meeting's small strip reaches it), and on a narrow screen the top-left pills (在线,
    // 设置) reach across: sit under whatever is showing in the card's column; if that leaves no room above the bottom
    // bar, sit left of the panels instead, and if there's no room there either (a phone), keep out of the way.
    const base = innerWidth <= 560 ? 8 : 12, w = box.offsetWidth, h = box.offsetHeight;
    let top = base, minLeft = innerWidth;
    const shown = [...document.querySelectorAll('.globby, .gmpanel, #meetMini, #topleft > *')].map((el) => [el, el.getBoundingClientRect()])
      .filter(([el, r]) => r.width && r.height && getComputedStyle(el).display !== 'none' && r.top < innerHeight / 2);
    for (const [el, r] of shown) if (r.right > innerWidth - w - base) { top = Math.max(top, r.bottom + 8); if (!el.closest('#topleft')) minLeft = Math.min(minLeft, r.left); }
    let right = base;
    if (top + h > innerHeight - 90 && minLeft < innerWidth) { right = innerWidth - minLeft + 8; top = base; }
    // and never under the app's toast (top centre) or a shared screen
    const L = innerWidth - right - w, R = innerWidth - right;
    for (const el of document.querySelectorAll('#toast, #screens')) {
      const r = el.getBoundingClientRect();
      if (r.height && getComputedStyle(el).display !== 'none' && r.left < R && r.right > L && r.top < top + h) top = Math.max(top, r.bottom + 8);
    }
    box.style.top = top + 'px'; box.style.right = right + 'px';
    box.style.visibility = innerWidth - right - w < 0 ? 'hidden' : '';
  }

  /** What 打招呼 on p's card says now: {icon, label, disabled, title}. */
  function greetButton(p, now) {
    const k = key(p), sent = sentAt.get(k) ?? 0, got = gotAt.get(k) ?? 0;
    // 请勿打扰 on the deck (not in a hut or 会议室, where the room's rules hold): the button says so; pressing it still
    // asks the office, which answers E_DND (greet_err below)
    const dndDeck = o.status?.(p)?.s === 'dnd' && !o.zoneAt(p.x, p.y) && !o.zoneAt(o.me().x, o.me().y);
    if (dndDeck) return { icon: 'dnd', label: t('greet.btn.dnd'), disabled: false, title: t('greet.btn.dndTip') };
    if (now - sent < PAIR_GAP_MS) return { icon: 'check', label: t('greet.btn.sent'), disabled: true, title: '' };
    return { icon: 'wave', label: t(got > sent && now - got < 120_000 ? 'greet.reply' : 'greet.btn.greet'), disabled: false, title: '' };
  }
  const replyTo = (p) => (gotAt.get(key(p)) ?? 0) > (sentAt.get(key(p)) ?? 0);
  function where(p) { const z = o.zoneAt(p.x, p.y); return z ? z.name : t(p.boat ? 'greet.where.sea' : 'greet.where.deck'); }
  function drawOtter(p) {
    const look = o.lookOf(p), sig = JSON.stringify(look);
    if (sig !== sheetSig) { sheetSig = sig; sheet = o.buildSheet(o.otterImgs, look); }
    g.imageSmoothingEnabled = false; g.clearRect(0, 0, 32, 32); g.drawImage(sheet, 0, 0, 32, 32, 0, 0, 32, 32);
  }
  function render(p, list, now) {
    if (!p) { card.style.display = 'none'; return; }
    card.style.display = 'flex';
    drawOtter(p);
    if (p.picture && pic.dataset.src !== p.picture) { pic.dataset.src = p.picture; pic.src = p.picture; }
    pic.hidden = !p.picture;
    put(q('.nm'), p.kind === 'agent' ? 'robot' : null, p.name); q('.nm').title = p.name;
    // 访客 (office/guests.ts; the name already ends in 「（访客）」): who let them in, first so it isn't cut off
    // 串门 (a visitor from another Raft server; the name ends in 「（来自 …）」): where they come from, likewise
    const bits = p.guest ? [['people', t('greet.guestBy', { name: p.guest.by || t('greet.member') })], ['pin', where(p)]] : p.visitor ? [['people', t('greet.visitorFrom', { server: p.visitor.server_name || t('greet.otherServer') })], ['pin', where(p)]] : [['pin', where(p)]];
    if (p.kind === 'agent') bits.push([null, t('greet.agent')]); else if (p.muted) bits.push(['mic_off', t('greet.muted')]); else if (p.trackName) bits.push(['mic', t('greet.talking')]);
    const s = o.status?.(p), stl = q('.stl');
    card.classList.toggle('dnd', s?.s === 'dnd');
    stl.hidden = !s; stl.className = 'stl' + (s ? ' ' + s.s : '');
    if (s) {
      const t = o.statusText(s), ic = o.statusIcon(s); // 「离开（自动）」, 「请勿打扰 · 写方案中 · 到 18:00」 (public/status.js)
      if (stl.dataset.k !== s.s + t) { stl.dataset.k = s.s + t; stl.replaceChildren(icon(ic), ' ', t); stl.title = t; }
    }
    const wh = q('.wh'), whKey = JSON.stringify(bits);
    if (wh.dataset.k !== whKey) { wh.dataset.k = whKey; wh.replaceChildren(...bits.flatMap(([i, t], n) => [...(n ? [' · '] : []), ...(i ? [icon(i), ' '] : []), t])); }
    const go = q('.go'), gb = greetButton(p, now);
    go.disabled = gb.disabled; go.title = gb.title; label(go, gb.icon, gb.label);
    const inv = q('.inv'), iv = o.invite ? o.invite.forCard(p) : null; // 邀请 to the game where I am, anyone (invite.js)
    inv.hidden = !iv;
    if (iv) { put(inv, iv.icon, iv.label); inv.disabled = !!iv.disabled; inv.title = iv.title ?? ''; inv.onclick = () => { iv.go?.(); setTimeout(tick, 300); }; }
    const here = q('.here'), hv = o.here ? o.here.forCard(p) : null; // 邀请到身边, anywhere (meetinvite.js); the label says how it went
    here.hidden = !hv;
    if (hv) { put(here, hv.icon, hv.label); here.disabled = !!hv.disabled; here.title = hv.title ?? ''; here.onclick = () => { hv.go?.(); setTimeout(tick, 300); }; }
    const sec = q('.sec'), sv = p.kind === 'agent' && o.talk ? o.talk.forCard(p) : null; // 和 TA 说话 (secretary.js)
    sec.hidden = !sv;
    if (sv) { put(sec, sv.icon, sv.label); sec.disabled = !!sv.disabled; sec.title = sv.title ?? ''; sec.onclick = () => { sv.go?.(); setTimeout(tick, 300); }; }
    const cl = q('.call'), cv = o.call ? o.call.forCard(p) : null; // 通话 (call.js): ring them, wherever they are on this floor
    cl.hidden = !cv;
    if (cv) { put(cl, cv.icon, cv.label); cl.disabled = !!cv.disabled; cl.title = cv.title ?? ''; cl.onclick = () => { cv.go?.(); setTimeout(tick, 300); }; }
    const kick = q('.kick'), kv = p.kind === 'agent' && o.door ? o.door.forCard(p) : null;
    kick.hidden = !kv;
    if (kv) { put(kick, kv.icon, kv.label); kick.onclick = () => kv.go(); }
    const walk = q('.walk'), isPinned = pinned?.id === p.id; walk.hidden = !isPinned || !o.walkTo;
    if (isPinned && walk.dataset.k !== 'w') { walk.dataset.k = 'w'; label(walk, 'pin', t('greet.walk')); }
    // 在 Raft 里私聊 (raft-links.js): their Raft page, whose 发消息 / 私信 opens the DM
    const rf = q('.rf'), url = o.raftProfile?.(p) ?? null;
    rf.hidden = !url;
    if (url && rf.dataset.href !== url) {
      rf.dataset.href = url; rf.href = url; put(rf, 'chat', t('greet.raft'));
      rf.title = t('greet.raftTip', { btn: t(p.kind === 'agent' ? 'greet.raftBtn.agent' : 'greet.raftBtn.person') });
    }
    const more = q('.more'); more.hidden = isPinned || list.length < 2; more.textContent = `${(list.indexOf(p) + 1) || 1}/${list.length} ›`;
  }

  function tick() {
    const now = Date.now(), list = candidates(now);
    if (pinned && !o.peers().get(pinned.id)) { pinned = null; o.unpinned?.(); }
    if (pinned) { shown = o.peers().get(pinned.id); place(); render(shown, list, now); return; }
    let cur = shown && o.peers().get(shown.id);
    if (cur && list.includes(cur)) goneAt = 0;
    else if (cur && (dismissed.get(key(cur)) ?? 0) <= now && o.peers().has(cur.id)) {
      // left earshot: stays a moment (unless someone else is in range now)
      goneAt ||= now;
      if (list.length || now - goneAt > LINGER_MS) cur = null;
    } else cur = null;
    if (!cur) { cur = list[pick % Math.max(1, list.length)] ?? list[0] ?? null; goneAt = 0; }
    shown = cur;
    place(); render(cur, list, now);
  }

  function greet(p, reply) {
    if (!p) return;
    o.send({ t: 'greet', to: p.id, ...(reply && { reply: true }) });
    sentAt.set(key(p), Date.now()); tick();
  }
  q('.go').onclick = () => { const p = shown && o.peers().get(shown.id); if (p) greet(p, replyTo(p)); };
  q('.walk').onclick = () => { const p = pinned && o.peers().get(pinned.id); if (p) o.walkTo?.(p); };
  q('.x').onclick = () => { if (pinned) { const p = o.peers().get(pinned.id); if (p) dismissed.set(key(p), Date.now() + DISMISS_MS); pinned = null; shown = null; o.unpinned?.(); tick(); return; } if (shown) dismissed.set(key(shown), Date.now() + DISMISS_MS); shown = null; tick(); };
  q('.rf').onclick = () => { const p = shown && o.peers().get(shown.id); if (p) o.toast(t('greet.raftToast', { name: p.name, btn: t(p.kind === 'agent' ? 'greet.raftBtn.agent' : 'greet.raftBtn.person') })); };
  q('.more').onclick = () => { const list = candidates(Date.now()); pick = (list.indexOf(shown) + 1) % Math.max(1, list.length); shown = list[pick] ?? null; tick(); };
  note.querySelector('button').onclick = () => {
    const p = noteFrom && o.peers().get(noteFrom);
    if (p) greet(p, true); else o.toast(t('greet.gone'));
    hideNote();
  };
  function hideNote() { note.style.display = 'none'; noteFrom = null; clearTimeout(noteTimer); }
  function showNote(text, from, canReply, agent) {
    const t = note.querySelector('.t'); t.replaceChildren(...(agent ? [icon('robot'), ' '] : []), text, ' ', icon('wave')); note.querySelector('button').style.display = canReply ? '' : 'none';
    note.style.display = 'flex'; noteFrom = from;
    clearTimeout(noteTimer); noteTimer = setTimeout(hideNote, NOTE_MS);
  }

  // H: greet whoever's on the card, or answer the greeting on screen. Not while typing anywhere (chat, game inputs).
  addEventListener('keydown', (e) => {
    if (e.key.toLowerCase() !== 'h' || e.repeat || e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return;
    const a = document.activeElement;
    if (a && (a.matches?.('input, textarea, select') || a.isContentEditable)) return;
    if (noteFrom && note.style.display !== 'none' && note.querySelector('button').style.display !== 'none') note.querySelector('button').click();
    else if (shown && !q('.go').disabled && card.style.display !== 'none') q('.go').click();
  });

  setInterval(tick, 400);
  // the language changed: the note's button, and the card's cached 走过去 / Raft link (the rest redraws every tick)
  onLang(() => { label(note.querySelector('button'), 'wave', t('greet.reply')); delete q('.walk').dataset.k; delete q('.rf').dataset.href; tick(); });

  return {
    /** 打招呼 for another card (the 小窗's, public/pip.js): {icon, label, disabled, title, go}; null if I can't see p. */
    forCard(p) {
      if (!o.me() || !o.peers().get(p.id)) return null;
      return { ...greetButton(p, Date.now()), go: () => { const q = o.peers().get(p.id); if (q) greet(q, replyTo(q)); } };
    },
    /** Show p's card until unpinned (pin(null)), near or far (a click on them: public/pick.js). */
    pin(p) { pinned = p ? { id: p.id } : null; if (p) dismissed.delete(key(p)); shown = p ?? null; goneAt = 0; tick(); },
    msg(m) {
      if (m.t === 'greet') {
        const p = o.peers().get(m.from), name = m.name || t('greet.otter'), agent = m.fromKind === 'agent';
        if (p) { gotAt.set(key(p), Date.now()); dismissed.delete(key(p)); shown = p; goneAt = 0; }
        const answered = p && Date.now() - (sentAt.get(key(p)) ?? 0) < PAIR_GAP_MS && m.kind !== 'reply';
        if (m.kind === 'reply') showNote(t('greet.note.reply', { name }), m.from, false, agent);
        else showNote(t('greet.note.greet', { name }), m.from, !answered, agent);
        tick();
      } else if (m.t === 'greet_err') {
        if (m.to) { const p = o.peers().get(m.to); if (p && m.code !== 'E_RATE') sentAt.delete(key(p)); }
        o.toast(m.code === 'E_DND' && m.message?.startsWith('you') ? t('greet.err.E_DND_self') : m.code && has(`greet.err.${m.code}`) ? t(`greet.err.${m.code}`) : t('greet.failed'));
        tick();
      }
      // greet_ok: the button already says 已打招呼; the wave is the office's emote
    },
  };
}
